import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SpotlightViewport = {
  width: number;
  height: number;
};

export type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export const buildCutoutPath = (
  viewport: SpotlightViewport,
  rect: SpotlightRect,
  padding: number,
  radius: number,
): string => {
  const x0 = rect.left - padding;
  const y0 = rect.top - padding;
  const w = rect.width + padding * 2;
  const h = rect.height + padding * 2;
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));

  const outer = `M0,0 H${viewport.width} V${viewport.height} H0 Z`;

  const inner =
    r === 0
      ? `M${x0},${y0} H${x0 + w} V${y0 + h} H${x0} Z`
      : [
          `M${x0 + r},${y0}`,
          `H${x0 + w - r}`,
          `A${r},${r} 0 0 1 ${x0 + w},${y0 + r}`,
          `V${y0 + h - r}`,
          `A${r},${r} 0 0 1 ${x0 + w - r},${y0 + h}`,
          `H${x0 + r}`,
          `A${r},${r} 0 0 1 ${x0},${y0 + h - r}`,
          `V${y0 + r}`,
          `A${r},${r} 0 0 1 ${x0 + r},${y0}`,
          "Z",
        ].join(" ");

  return `${outer} ${inner}`;
};

export type SpotlightOverlayProps = {
  target: HTMLElement;
  padding?: number;
  radius?: number;
  variant?: "dim" | "ring";
};

export const SpotlightOverlay = ({
  target,
  padding = 6,
  radius = 8,
  variant = "dim",
}: SpotlightOverlayProps) => {
  const [path, setPath] = useState("");
  const [ringRect, setRingRect] = useState<SpotlightRect | null>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    let lastKey = "";
    const track = () => {
      const rect = target.getBoundingClientRect();
      const key = `${rect.top}:${rect.left}:${rect.width}:${rect.height}:${window.innerWidth}:${window.innerHeight}`;
      if (key !== lastKey) {
        lastKey = key;
        setRingRect({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });
        setPath(
          buildCutoutPath(
            { width: window.innerWidth, height: window.innerHeight },
            rect,
            padding,
            radius,
          ),
        );
      }
      frameRef.current = requestAnimationFrame(track);
    };
    track();

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [target, padding, radius]);

  if (variant === "ring") {
    return createPortal(
      ringRect ? (
        <div
          data-testid="onboarding-hint-ring"
          aria-hidden="true"
          className="fixed z-[1100] pointer-events-none rounded-lg ring-2 ring-green-500 shadow-[0_0_0_6px_rgba(0,200,137,0.2)] motion-safe:animate-pulse"
          style={{
            top: ringRect.top,
            left: ringRect.left,
            width: ringRect.width,
            height: ringRect.height,
            borderRadius: radius,
          }}
        />
      ) : null,
      document.body,
    );
  }

  return createPortal(
    <svg className="fixed inset-0 z-[1100] w-screen h-screen" style={{ pointerEvents: "none" }}>
      <path
        fillRule="evenodd"
        fill="black"
        fillOpacity={0.5}
        style={{ pointerEvents: "auto" }}
        d={path}
      />
    </svg>,
    document.body,
  );
};

export default SpotlightOverlay;
